import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

export interface EnergyData {
  solarOutput: number; // kW
  windOutput: number; // kW
  batteryLevel: number; // %
  gridStatus: "importing" | "exporting" | "neutral";
  timestamp: Date;
  location: {
    lat: number;
    lng: number;
  };
}

export interface WeatherData {
  temperature: number; // Celsius
  humidity: number; // %
  windSpeed: number; // m/s
  windDirection: number; // degrees
  solarIrradiance: number; // W/m²
  timestamp: Date;
}

export interface EnergyState {
  currentEnergy: EnergyData | null;
  historicalEnergy: EnergyData[];
  weatherData: WeatherData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

const initialState: EnergyState = {
  currentEnergy: null,
  historicalEnergy: [],
  weatherData: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunks for API calls
export const fetchCurrentEnergyData = createAsyncThunk(
  "energy/fetchCurrent",
  async (userId: string) => {
    const response = await fetch(`/api/energy/current/${userId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch energy data");
    }
    return response.json();
  },
);

export const fetchHistoricalEnergyData = createAsyncThunk(
  "energy/fetchHistorical",
  async ({ userId, days }: { userId: string; days: number }) => {
    const response = await fetch(
      `/api/energy/historical/${userId}?days=${days}`,
    );
    if (!response.ok) {
      throw new Error("Failed to fetch historical data");
    }
    return response.json();
  },
);

export const fetchWeatherData = createAsyncThunk(
  "energy/fetchWeather",
  async (location: string) => {
    const response = await fetch(`/api/weather/${location}`);
    if (!response.ok) {
      throw new Error("Failed to fetch weather data");
    }
    return response.json();
  },
);

const energySlice = createSlice({
  name: "energy",
  initialState,
  reducers: {
    setCurrentEnergy: (state, action: PayloadAction<EnergyData>) => {
      state.currentEnergy = action.payload;
      state.lastUpdated = new Date();
    },
    addHistoricalData: (state, action: PayloadAction<EnergyData>) => {
      state.historicalEnergy.push(action.payload);
      // Keep only last 1000 records
      if (state.historicalEnergy.length > 1000) {
        state.historicalEnergy = state.historicalEnergy.slice(-1000);
      }
    },
    setWeatherData: (state, action: PayloadAction<WeatherData>) => {
      state.weatherData = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetEnergyState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch current energy data
      .addCase(fetchCurrentEnergyData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentEnergyData.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEnergy = action.payload;
        state.lastUpdated = new Date();
      })
      .addCase(fetchCurrentEnergyData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch energy data";
      })
      // Fetch historical energy data
      .addCase(fetchHistoricalEnergyData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHistoricalEnergyData.fulfilled, (state, action) => {
        state.loading = false;
        state.historicalEnergy = action.payload;
      })
      .addCase(fetchHistoricalEnergyData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch historical data";
      })
      // Fetch weather data
      .addCase(fetchWeatherData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeatherData.fulfilled, (state, action) => {
        state.loading = false;
        state.weatherData = action.payload;
      })
      .addCase(fetchWeatherData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch weather data";
      });
  },
});

export const {
  setCurrentEnergy,
  addHistoricalData,
  setWeatherData,
  clearError,
  resetEnergyState,
} = energySlice.actions;

export default energySlice.reducer;
